import { ADAPTIVE_CAPACITY_MODULES, INSUFFICIENT_EVIDENCE } from "./Consts";
import questionModules from "./Questions";

const csvColumnNames = [
  'Kingdom',
  'Taxonomic Group',
  'Species Scientific Name',
  'Common Name',
  'Population or other designation',
  'Geographic Area',
  'G-Rank',
  'S-Rank',

  'State',
  'RCP4.5',
  'RCP8.5',

  'B1',
  'C1',
  'C2',
  'C3',
  'C4',
  'C5',
  'D1a',
  'D1b',
  'D2',
  'D3',
  'D4',
  'D5',
  'D6',
  'D7',
  'D8',
  'E1',
  'E2',
  'E3',
  'F1',
  'F2',
  'F3',
  'G1',
  'G2',
  'G3',
  'G4',
  'G5',
  'H1',
  'H2',
  'H3a',
  'H3b',
  'H4',
  'H5a',
  'H5b',
  'H6',
  'H7',
  'H8',
  'I1',
  'I2',
  'I3',
  'I4',
  'I5',
  'J1',
  'J2',
  'J3',
  'J4',
  'J5',
  'K1',
  'K2',
  'K3',
  'K4',

  'Gen Info',
  'Mod A RCP4.5',
  'Mod A RCP 8.5',
  'Mod B',
  'Mod C',
  'Mod D',
  'Mod E',
  'Mod F',
  'Mod G',
  'Mod H',
  'Mod I',
  'AC',
  'Mod J',
  'Mod K',
  'Min RCP4.5',
  'Min RCP8.5',

  'Clim Exp 4.5 Value',
  'Clim Exp 4.5 Category',
  'Clim Exp 8.5 Value',
  'Clim Exp 8.5 Category',
  'AC Score',
  'AC Category',
  'CCVI Cat 4.5',
  'CCVI Cat 8.5',

  'Assessor',
  'Date of assessment',
  'Assessment Notes'
];

// Assessment form keys in the order they appear in the workbook
const assessmentInfoColumns = [
  'kingdom',
  'majorTaxonomicGroup',
  'scientificName',
  'commonName',
  'population',
  'geographicArea',
  'gRank',
  'sRank'
];

/** get "unknown value" to show in the CSV based on the module's scale */
const getUnknownAnswerVal = (module) => {
  if (module?.scale?.options[0] === 'GI') {
    return 'Unknown';
  }
  return 'U/NA';
}

const getQuestionVal = (question, module, surveyAnswers, t, isValidForKingdom = true) => {
  if (!isValidForKingdom) {
    return getUnknownAnswerVal(module);
  }
  // the workbook uses 'U/NA' for unaswered questions:
  const rawAssessmentVal = surveyAnswers[question.label];
  if (rawAssessmentVal === INSUFFICIENT_EVIDENCE) {
    return getUnknownAnswerVal(module);
  }
  // the scale options are defined in the order they appear in the help dialog,
  // while the number values in surveyAnswers are the reversed index (0 is the last option):
  const scaleDegreeKey = [...module.scale.options].reverse()[rawAssessmentVal];
  return t(`scales.${scaleDegreeKey}`, { context: 'csv' });
}

const getResultVal = (rawValue, t, isOverallVal) => {
  if (typeof rawValue === 'string') {
    if (!isOverallVal && rawValue === 'insufficientEvidence') {
      return 'Insufficient Data';
    }
    return t(`scoring.${rawValue}`, { context: 'csv' });
  }
  if (rawValue === INSUFFICIENT_EVIDENCE) {
    return 'NA';
  }
  return rawValue;
}

const getAssessmentFormValue = (columnId, assessed, t) => {
  const rawAssessmentVal = assessed[columnId];
  // For domain values, the workbook uses human-readable names:
  if (columnId === 'kingdom') {
    // including context, in case we want to change the text on the website independently:
    return t(`kingdoms.${rawAssessmentVal}`, { context: 'csv' });
  }
  if (columnId === 'majorTaxonomicGroup') {
    return t(`majorTaxonomicGroups.${rawAssessmentVal}`, { context: 'csv' });
  }
  return rawAssessmentVal;
}

const getCompletenessValue = (value) => {
  if (typeof value === 'boolean') {
    return value ? 'TRUE' : 'FALSE';
  } else if (typeof value === 'number' || typeof value === 'string') {
    return `${value}%`;
  } else {
    throw new Error(`Attempted to add invalid data completeness value to CSV: ${value}`);
  }
}

/**
 * Calculates the CSV data to be used with the CSVLink component
 * @param {object} results An object with all values derived from the assessment state
 * @param {object} globalAssessmentState The object containing the assessment's state
 * @param {boolean} hasGraphics If graphics exist defining the area assessed
 * @param {function} t translation function - CSV domain values use human-readable names
 * @returns An array of two arrays: 1. csv column names, 2. values to copy into the CCVI workbook
 */
export const getCsvData = (results, globalAssessmentState, hasGraphics, t) => {
  const { moduleCompleteness, exposureResultInfos, adaptiveCapacityInfo } = results;
  const { assessed, surveyAnswers, lastModified, stateName } = globalAssessmentState;
  // Note: these will be ordered the other way around in the csv columns:
  const [rcp85ResultInfo, rcp45ResultInfo] = exposureResultInfos;
  const { kingdom } = assessed;

  const colVals = [];

  /** Throws an error if the columnm name at the index of the latest column value what's expected */
  const testExpectedColName = (expected) => {
    const curColName = csvColumnNames[colVals.length - 1];
    if ( curColName !== expected) {
      throw new Error(`CSV column name mismatch: for ${curColName} (expected ${expected})`);
    }
  };

  // Start with adding the Assessment Form columns:
  assessmentInfoColumns.forEach(colId => {
    colVals.push(getAssessmentFormValue(colId, assessed, t));
  });

  // Climate Exposure columns:
  const fixRCPVAl = val => val === INSUFFICIENT_EVIDENCE ? '' : val;
  const fixedStateName = stateName || (hasGraphics ? 'User-provided shape from web tool' : '');
  colVals.push(fixedStateName);
  colVals.push(fixRCPVAl(rcp45ResultInfo.climateExposure));
  colVals.push(fixRCPVAl(rcp85ResultInfo.climateExposure));


  // Question Modules:
  // The following columns are from "question modules" (its metadata defined in questionModules):
  Object.entries(questionModules).forEach(([moduleKey, module]) => {
    module.questions.forEach((question, idx) => {
      // for kingdom-specific questions, add two columns (one for animals and one for plants):
      if (question.overridesByKingdom) {
        colVals.push(getQuestionVal(question, module, surveyAnswers, t, kingdom === 'animalia'));
        testExpectedColName(`${moduleKey}${idx + 1}a`);
        colVals.push(getQuestionVal(question, module, surveyAnswers, t, kingdom === 'plantae'));
        testExpectedColName(`${moduleKey}${idx + 1}b`);
      } else {
        colVals.push(getQuestionVal(question, module, surveyAnswers, t));
        testExpectedColName(`${moduleKey}${idx + 1}`);
      }
    });
  });

  // Data Completeness:
  // Gen Info has to be complete in order to get to export:
  colVals.push(getCompletenessValue(true));
  // Add Mod A RCP4.5 and RCP8.5 completeness values (if one's complete the other is):
  colVals.push(getCompletenessValue(moduleCompleteness.A === 100));
  colVals.push(getCompletenessValue(moduleCompleteness.A === 100));
  // Add boolean for Mod B:
  colVals.push(getCompletenessValue(moduleCompleteness.B === 100));
  ADAPTIVE_CAPACITY_MODULES.forEach(moduleKey => {
    colVals.push(getCompletenessValue(moduleCompleteness[moduleKey]));
  })
  colVals.push(getCompletenessValue(moduleCompleteness.adaptiveCapacity));
  colVals.push(getCompletenessValue(moduleCompleteness.J));
  colVals.push(getCompletenessValue(moduleCompleteness.K));
  // Add overall RCP4.5 and RCP8.5 completeness (if one's complete the other is):
  colVals.push(getCompletenessValue(moduleCompleteness.overallCategory));
  colVals.push(getCompletenessValue(moduleCompleteness.overallCategory));

  // Results:
  colVals.push(getResultVal(rcp45ResultInfo.climateExposure, t));
  colVals.push(getResultVal(rcp45ResultInfo.climateExposureCategory, t));
  colVals.push(getResultVal(rcp85ResultInfo.climateExposure, t));
  colVals.push(getResultVal(rcp85ResultInfo.climateExposureCategory, t));
  colVals.push(getResultVal(adaptiveCapacityInfo.score, t));
  colVals.push(getResultVal(adaptiveCapacityInfo.categoryKey, t));
  colVals.push(getResultVal(rcp45ResultInfo.overallCategory, t, true));
  colVals.push(getResultVal(rcp85ResultInfo.overallCategory, t, true));

  // Additional Metadata columns:
  colVals.push(getAssessmentFormValue('assessor', assessed, t));
  colVals.push(lastModified ? new Intl.DateTimeFormat('en-US').format(new Date(lastModified)) : '');
  colVals.push(getAssessmentFormValue('assessmentNotes', assessed, t));
  testExpectedColName('Assessment Notes');
  return [csvColumnNames, colVals];
}